// Story 8.6 (ADO-2015), D-9 — the dates on the order confirmation (Figma 5464:87367): the
// delivery day the order was accepted for, "Thu 24 Sep 2026", and when it was sent,
// "Wed 23 Sep 2026 at 1:51pm".
//
// The delivery day is a calendar DAY (`YYYY-MM-DD`, already the Dublin day the order was accepted
// for), not an instant, so it is formatted in UTC: formatting it in the browser's zone could move
// it a day. The sent-at instant is shown in the depot's zone (Europe/Dublin), as the cut-off copy
// is (`cutOffDisplay.ts`), never the browser's.

import { formatDateTimeWithYear, formatTimeWithDateUnlessSameDay } from './cutOffDisplay';

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatter = new Intl.DateTimeFormat('en-IE', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** The weekday, day, three-letter month and year of a `YYYY-MM-DD` day, or `null` for anything
 *  else. */
function dayParts(
  isoDay: string,
): { weekday: string; day: string; month: string; year: string } | null {
  const match = DAY.exec(isoDay);
  if (match === null) return null;
  const instant = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parts = formatter.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  // en-IE abbreviates September as "Sept"; the design uses three letters.
  return {
    weekday: get('weekday'),
    day: get('day'),
    month: get('month').slice(0, 3),
    year: get('year'),
  };
}

/** "Fri 2 Oct 2026" for `2026-10-02`, or the input unchanged when it is not a `YYYY-MM-DD` day. */
export function formatDeliveryDay(isoDay: string): string {
  const parts = dayParts(isoDay);
  return parts === null ? isoDay : `${parts.weekday} ${parts.day} ${parts.month} ${parts.year}`;
}

/** "Wed 23 Sep 2026 at 1:51pm" in Dublin time, or the input unchanged when it is not an instant. */
export function formatSentAt(instantIso: string): string {
  if (Number.isNaN(Date.parse(instantIso))) return instantIso;
  return formatDateTimeWithYear(instantIso);
}

/** Story 8.3 (ADO-2012), D-27: when an order was sent, on cards 10 and 11 (the designer's
 *  "sent at 11:42"): "4:46pm" when it was sent on `nowMs`'s Dublin day, else "4:46pm on 14 Oct
 *  2026", in Dublin time whatever the viewer's zone. The input unchanged when it is not an
 *  instant. */
export function formatSentTime(instantIso: string, nowMs: number): string {
  if (Number.isNaN(Date.parse(instantIso))) return instantIso;
  return formatTimeWithDateUnlessSameDay(instantIso, nowMs);
}
